import React, { useLayoutEffect, useRef, useState } from "react";
import { useComponentLogger } from "../../shared/logging";
import { isIE11 } from "../../shared/utilities";
import { Heading, HeadingProps } from "../Heading/Heading";
import { StopPropagation } from "../StopPropagation/StopPropagation";
import { Text, TextProps } from "../Text/Text";
import { TooltipHost } from "../TooltipHost/TooltipHost";

const fallbackLineHeight = 1.4;
const defaultNumberOfLines = 1;

const getComputedStyle = (el: Element, style: string) =>
    window.getComputedStyle(el).getPropertyValue(style);

const stringToPx = (size: string) => {
    let sizeNumber = size;
    if (sizeNumber.includes("px")) {
        sizeNumber = sizeNumber.replace("px", "");
    }

    // unary "+" operator converts to a number
    return +sizeNumber;
};

const getFontSize = (el: Element) => stringToPx(getComputedStyle(el, "font-size"));

const getLineHeight = (el: Element) => {
    const lineHeight: string = getComputedStyle(el, "line-height");

    // For Fluent Text, the actual line height (in pixels) is returned. The below line is just a safety fallback,
    // but won't be hit if we use Fluent's Text component
    if (lineHeight === "normal") {
        return fallbackLineHeight * getFontSize(el);
    }

    return stringToPx(lineHeight);
};

const getMaxElementHeight = (lineHeight: number, lines: number) =>
    Math.round(lineHeight * lines);

/**
 * Checks whether a given element's text is truncated by seeing whether its `scrollHeight` (the height required
 * to show all the context in the element) is greater than the maximum height the element can be, as calculated by
 * the line-height and the number of lines after which we cut off text.
 *
 * @param el the span that has text that might be truncated
 * @param lines the number of lines of text to show before truncation
 */
const checkIsTruncated = (el: Element, lines: number): boolean => {
    const lineHeight = getLineHeight(el);
    const maxHeight = getMaxElementHeight(lineHeight, lines);
    const actualHeight = el.scrollHeight;

    return actualHeight > maxHeight;
};

export interface TruncatedTextBaseProps {
    /**
     * Number of lines of text to show before truncation.
     * Defaults to 1.
     */
    readonly lines?: number;

    /**
     * If `true`, prevents events (e.g. click events) from being propagated.
     * Defaults to `true`.
     */
    readonly stopPropagation?: boolean;

    /**
     * If `true`, allows the focus with tab navigation
     * Defaults to `true`.
     */
    readonly onTruncatedFocusable?: boolean;

    /**
     * Optional line height for text, defaults to 1
     */
    readonly lineHeight?: number;

}

export interface TruncatedTextTextProps extends TextProps, TruncatedTextBaseProps {
    readonly type?: "text";
}

export interface TruncatedTextHeadingProps extends HeadingProps, TruncatedTextBaseProps {
    readonly type: "heading";
}

export type TruncatedTextProps = TruncatedTextTextProps | TruncatedTextHeadingProps;

export const TruncatedText: React.FC<TruncatedTextProps> = ({
    type,
    lines = defaultNumberOfLines,
    stopPropagation = true,
    onTruncatedFocusable = true,
    lineHeight,
    ...otherProps
}) => {
    useComponentLogger("TruncatedText", otherProps.id);

    const spanRef = useRef<HTMLSpanElement>(null);
    const [fontSize, setFontSize] = useState<number | undefined>(undefined);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncation = () => {
        if (spanRef.current) {
            setIsTruncated(checkIsTruncated(spanRef.current, lines));
        }
    };

    const changeFontSize = () => {
        if (spanRef.current?.firstElementChild) {
            setFontSize(getFontSize(spanRef.current.firstElementChild));
        }
    };

    useLayoutEffect(() => {
        window.addEventListener("resize", changeFontSize);
        window.addEventListener("resize", checkTruncation);
        return () => {
            window.removeEventListener("resize", checkTruncation);
            window.removeEventListener("resize", changeFontSize);
        };
    }, []);

    useLayoutEffect(() => {
        changeFontSize();
    }, [spanRef]);

    useLayoutEffect(() => {
        checkTruncation();
    }, [fontSize, otherProps.children]);

    // data-is-focusable / tabIndex are set to allow truncated Text to be navigated to by the keyboard, allowing
    // keyboard users to trigger the Tooltip
    const keyboardFocusPropsIfTruncated = isTruncated && onTruncatedFocusable ? {
        "data-is-focusable": true,
        tabIndex: 0
    } : null;

    const ie11ScrollbarInstead: React.CSSProperties | undefined = isIE11() && spanRef.current ? {
        height: getMaxElementHeight(getLineHeight(spanRef.current), lines),
        overflowY: "scroll"
    } : undefined;

    const baseContent = (
        <span
            ref={spanRef}
            {...keyboardFocusPropsIfTruncated}
            style={{
                fontSize,
                overflow: "hidden",
                boxSizing: "content-box",
                whiteSpace: "normal",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: lines,
                color: otherProps.colorOverride ?? undefined,
                lineHeight: lineHeight ?? undefined,
                ...ie11ScrollbarInstead
            }}
        >
            {type === "heading" ? <Heading {...otherProps as HeadingProps} /> : <Text {...otherProps as TextProps} />}
        </span>
    );

    if (isTruncated && !isIE11()) {
        const tooltipHostContent = (
            <TooltipHost
                tooltipText={otherProps.children as JSX.Element}
                tooltipPosition="bottom-left"
            >
                {baseContent}
            </TooltipHost>
        );

        return stopPropagation ? (
            <StopPropagation>
                {tooltipHostContent}
            </StopPropagation>
        ) : tooltipHostContent;
    } else {
        return baseContent;
    }
};
